import {
  type PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react'

import {
  getGuideTourSkippedStepNoticeMs,
  setGuideTourMobileViewportReader,
} from '../config'
import type { GuideTourStepInteractionAction } from '../notifications/types'
import type { GuideTourStepInteractionDisplay } from '../notifications/interaction'
import {
  notifyGuideTourStepInteraction,
  notifyGuideTourUnavailableOnPage,
} from '../notifications/interaction'
import {
  createGuideTourUnavailableStepSkipper,
  getGuideTourTargetAvailability,
  useGuideTourMissingTargetSkip,
  useGuideTourMobileViewport,
  useGuideTourStepReadiness,
  type GuideTourUnavailableStepSkipKind,
} from '../hooks'
import { GuideTourOverlay } from '../overlay'
import { GuideTourSpotlightShield } from '../overlay/GuideTourSpotlightShield'
import { GuideTourForceClickAffordance } from '../overlay/GuideTourForceClickAffordance'
import {
  isGuideTourPageScrollLocked,
  lockGuideTourPageScroll,
  releaseOrphanedGuideTourPageScrollLock,
} from '../overlay/scrollLock'
import { useGuideTourTarget } from '../overlay/useGuideTourTarget'
import { GUIDE_TOUR_TARGET_MISSING_DELAY_MS } from '../overlay/utils'
import {
  clearGuideTourProgress,
  finishGuideTour,
  getGuideTourProgress,
  isGuideTourSeen,
  setGuideTourProgress,
} from '../persistence'
import {
  getGuideTourStepGateOptions,
  GUIDE_TOUR_STEP_ACTIVATION_MAX_WAIT_MS,
  isGuideTourActivationStepTargetMissing,
  isGuideTourStepInModalLayer,
  waitForGuideTourStepActivatable,
} from '../routing/activation'
import { listGuideTourNotifications } from '../routing/notifications'
import {
  beginRouteTourChainSession,
  clearRouteTourChainSession,
  getRouteTourChainSessionInfo,
  getRouteTourQueueInfo,
  GUIDE_TOUR_ROUTE_CHAIN_COOLDOWN_MS,
  hasMoreUnseenRouteToursAfter,
  isGuideTourRouteChainCooldownActive,
  isRouteTourChainSessionActive,
} from '../routing/queue'
import {
  getGuideTourOriginRoute,
  shouldRestoreGuideTourOriginRouteOnExit,
} from '../routing/tourOriginRoute'
import {
  getGuideTourStepRoute,
  isGuideTourSuppressedOnMobile,
  matchGuideTourRoute,
} from '../routing/utils'
import {
  canGoToNextGuideTourStep,
  createInitialGuideTourState,
  getCurrentGuideTourStep,
  guideTourReducer,
  isGuideTourLastStep,
  shouldGuideTourCompleteOnNext,
} from '../state'
import { GuideTourStepCard } from '../step-card'
import { GuideTourActivationLoadingCard } from '../step-card/GuideTourActivationLoadingCard'
import {
  GUIDE_TOUR_BLOCKED_TARGET_INTERACTION_WARNING_MS,
  type GuideTourDefinition,
  type GuideTourRegistry,
} from '../types'
import {
  closeGuideTourModalIfOpenedByForceClick,
  finalizeGuideTourForcedScrollOnExit,
  isGuideTourStepInForceClickModalFlow,
} from '../utils/modals'
import {
  clearGuideTourRouteScrollState,
  findPreviousReachableGuideTourStepIndex,
  forceClickGuideTourTarget,
  getGuideTourStepForceClickAffordanceTargetId,
  getGuideTourStepScrollTargetId,
  hasGuideTourStepTarget,
  hasPreviousReachableGuideTourStep,
  shouldGuideTourForceScrollToTarget,
} from '../utils/targets'
import {
  appendGuideTourSkippedStepEntry,
  countAvailableGuideTourSteps,
  createGuideTourSkippedStepEntry,
  findNextAvailableGuideTourStepIndex,
  getAvailableGuideTourStepNumber,
  isGuideTourStepExpired,
  resolveGuideTourActivationStepIndex,
  resolveGuideTourForRuntime,
  shouldTreatGuideTourAsCompleted,
  type GuideTourSkippedStepNotice,
} from '../utils/tourRuntime'
import type { GuideTourDismissalRecord } from './tourDismissal'
import {
  clearGuideTourDismissalForTour,
  clearGuideTourDismissalOutsidePath,
  isGuideTourDismissedOnPath,
} from './tourDismissal'
import { GuideTourContext } from './guideTourContext'
import type { GuideTourContextValue, GuideTourLaunchMode } from './types'

type GuideTourProviderProps = PropsWithChildren & {
  registry: GuideTourRegistry
}

export const GuideTourProvider = ({
  children,
  registry,
}: GuideTourProviderProps) => {
  const [state, dispatch] = useReducer(
    guideTourReducer,
    undefined,
    createInitialGuideTourState
  )
  const stateRef = useRef(state)
  stateRef.current = state
  const tourUnavailableSummaryLatchedRef = useRef(false)
  const dismissedTourRef = useRef<GuideTourDismissalRecord | null>(null)
  const routeTourPausedPathnameRef = useRef<string | null>(null)
  const routeTourChainCooldownUntilRef = useRef(0)
  const [routeTourChainCooldownUntil, setRouteTourChainCooldownUntil] =
    useState(0)
  const [
    blockedTargetInteractionFeedbackKey,
    setBlockedTargetInteractionFeedbackKey,
  ] = useState(0)
  const [
    isBlockedTargetInteractionWarningVisible,
    setIsBlockedTargetInteractionWarningVisible,
  ] = useState(false)
  const [skippedStepNotice, setSkippedStepNotice] =
    useState<GuideTourSkippedStepNotice | null>(null)
  const [isSkippedStepNoticeVisible, setIsSkippedStepNoticeVisible] =
    useState(false)
  const [isSkippedStepsSummaryActive, setIsSkippedStepsSummaryActive] =
    useState(false)
  const [originRouteRestore, setOriginRouteRestore] = useState<string | null>(
    null
  )
  const [pendingGuideTourLaunch, setPendingGuideTourLaunch] =
    useState<GuideTourContextValue['pendingGuideTourLaunch']>(null)
  const [notificationsRevision, setNotificationsRevision] = useState(0)
  const [isGuideTourActivationPending, setIsGuideTourActivationPending] =
    useState(false)
  const guideTourActivationTimeoutRef = useRef<number | null>(null)
  const refreshGuideTourNotifications = useCallback(() => {
    setNotificationsRevision(revision => revision + 1)
  }, [])
  const isMobileViewport = useGuideTourMobileViewport()

  useEffect(() => {
    return () => {
      if (guideTourActivationTimeoutRef.current !== null) {
        window.clearTimeout(guideTourActivationTimeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    setGuideTourMobileViewportReader(() => isMobileViewport)

    return () => {
      setGuideTourMobileViewportReader(null)
    }
  }, [isMobileViewport])

  useEffect(() => {
    refreshGuideTourNotifications()
  }, [isMobileViewport, refreshGuideTourNotifications])

  useEffect(() => {
    const currentState = stateRef.current

    if (
      currentState.status !== 'active' ||
      !currentState.activeTour?.hideMobile ||
      !isMobileViewport
    ) {
      return
    }

    dispatch({ type: 'DISMISS' })
  }, [isMobileViewport])

  useEffect(() => {
    listGuideTourNotifications(registry)
    refreshGuideTourNotifications()
  }, [registry, refreshGuideTourNotifications])

  useEffect(() => {
    let timeoutId: number | undefined

    const scheduleRefresh = () => {
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId)
      }

      timeoutId = window.setTimeout(() => {
        refreshGuideTourNotifications()
      }, 200)
    }

    const observer = new MutationObserver(scheduleRefresh)

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['id', 'data-testid', 'hidden'],
    })
    scheduleRefresh()

    return () => {
      observer.disconnect()

      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId)
      }
    }
  }, [refreshGuideTourNotifications])
  const [currentPathname, setCurrentPathname] = useState(
    typeof window === 'undefined' ? '/' : window.location.pathname
  )
  const currentPathnameRef = useRef(currentPathname)
  const tourSessionAutoStartRef = useRef(false)

  const notifyStepInteraction = useCallback(
    (
      action: GuideTourStepInteractionAction,
      display?: GuideTourStepInteractionDisplay
    ) => {
      const currentState = stateRef.current
      const tour = currentState.activeTour

      if (!tour || currentState.status !== 'active') {
        return
      }

      const stepIndex = currentState.currentStepIndex

      const stepDisplay = display ?? {
        currentStep: getAvailableGuideTourStepNumber(tour, stepIndex),
        totalStep: countAvailableGuideTourSteps(tour),
      }

      notifyGuideTourStepInteraction(
        action,
        tour,
        stepIndex,
        currentPathnameRef.current,
        stepDisplay,
        action === 'start'
          ? { autoStart: tourSessionAutoStartRef.current }
          : undefined
      )
    },
    []
  )

  const activeModalFlowRef = useRef<{
    tour: GuideTourDefinition
    stepIndex: number
  } | null>(null)
  const currentStep = getCurrentGuideTourStep(state)
  const isCurrentStepReady = useGuideTourStepReadiness(
    currentStep,
    state.activeTour,
    state.currentStepIndex,
    state.status === 'active'
  )
  const isInModalLayer =
    state.activeTour !== null &&
    isGuideTourStepInModalLayer(state.activeTour, state.currentStepIndex)
  const hasTarget = hasGuideTourStepTarget(currentStep)
  const targetId =
    state.status === 'active' && hasTarget ? currentStep.targetId : null
  const targetActivationKey =
    state.status === 'active' && state.activeTourId
      ? `${state.activeTourId}:${state.currentStepIndex}`
      : null
  const isRouteTourChain = isRouteTourChainSessionActive(currentPathname)
  const scrollOptions = { isRouteTourChain }
  const forceScrollToTarget =
    state.status === 'active' &&
    Boolean(targetId) &&
    shouldGuideTourForceScrollToTarget(
      currentStep,
      state.activeTour,
      scrollOptions
    )
  const scrollTargetId =
    forceScrollToTarget && targetId
      ? getGuideTourStepScrollTargetId(currentStep, targetId)
      : null
  const targetBounds = useGuideTourTarget(targetId, {
    enabled: state.status === 'active' && isCurrentStepReady,
    scrollTargetId,
    forceScrollToTarget,
    activationKey: targetActivationKey,
  })
  const forceClickAffordanceTargetId =
    state.status === 'active' && isCurrentStepReady
      ? getGuideTourStepForceClickAffordanceTargetId(currentStep)
      : null
  const forceClickTargetBounds = useGuideTourTarget(
    forceClickAffordanceTargetId,
    {
      enabled: Boolean(forceClickAffordanceTargetId),
      activationKey: targetActivationKey,
      forceScrollToTarget: false,
    }
  )

  const getGuideTour = useCallback(
    (tourId: string) => registry.getGuideTour(tourId),
    [registry]
  )

  const clearSkippedStepNotice = useCallback(() => {
    setSkippedStepNotice(null)
    setIsSkippedStepNoticeVisible(false)
    setIsSkippedStepsSummaryActive(false)
  }, [])

  const dismissTourUnavailableOnPage = useCallback(() => {
    const currentState = stateRef.current
    const tour = currentState.activeTour

    clearSkippedStepNotice()
    tourUnavailableSummaryLatchedRef.current = false

    if (tour) {
      closeGuideTourModalIfOpenedByForceClick(
        tour,
        currentState.currentStepIndex
      )
      finalizeGuideTourForcedScrollOnExit(
        tour,
        currentState.currentStepIndex,
        currentPathnameRef.current,
        registry,
        'dismissed'
      )
    }

    dispatch({ type: 'DISMISS' })
  }, [clearSkippedStepNotice, registry])

  const clearOriginRouteRestore = useCallback(() => {
    setOriginRouteRestore(null)
  }, [])

  const clearPendingGuideTourLaunch = useCallback(() => {
    setPendingGuideTourLaunch(null)
  }, [])

  const guideTourNotifications = useMemo(
    () =>
      listGuideTourNotifications(
        registry,
        isGuideTourSeen,
        new Date(),
        currentPathname,
        notificationsRevision
      ),
    [registry, notificationsRevision, currentPathname]
  )

  const getGuideTourNotifications = useCallback(
    () => guideTourNotifications,
    [guideTourNotifications]
  )

  const getInProgressGuideTours = useCallback(
    () => guideTourNotifications.filter(item => item.kind === 'interrupted'),
    [guideTourNotifications]
  )

  const recordAutoSkippedStep = useCallback(
    (
      stepIndex: number,
      skipKind: GuideTourUnavailableStepSkipKind | 'expired'
    ) => {
      if (tourUnavailableSummaryLatchedRef.current) {
        return
      }

      const tour = stateRef.current.activeTour

      if (!tour) {
        return
      }

      const reason = skipKind === 'expired' ? 'expired' : 'unavailable-target'
      const entry = createGuideTourSkippedStepEntry(tour, stepIndex, reason)

      setSkippedStepNotice(current =>
        appendGuideTourSkippedStepEntry(current, entry)
      )
      setIsSkippedStepNoticeVisible(true)
    },
    []
  )

  const start = useCallback(
    (
      tourId: string,
      options?: {
        stepIndex?: number
        viewedStepCount?: number
        resume?: boolean
        autoStart?: boolean
      }
    ) => {
      const tour = registry.getGuideTour(tourId)

      if (!tour) {
        return
      }

      if (isGuideTourSuppressedOnMobile(tour)) {
        return
      }

      const activeTour = resolveGuideTourForRuntime(tour)

      const existingProgress = getGuideTourProgress(tour.id, tour.version)
      const requestedStepIndex = options?.stepIndex ?? 0
      const resolvedStepIndex =
        resolveGuideTourActivationStepIndex(activeTour, requestedStepIndex) ??
        requestedStepIndex
      const isResume =
        options?.resume === true ||
        (existingProgress !== null &&
          existingProgress.currentStepIndex === requestedStepIndex)

      if (!isResume) {
        clearGuideTourProgress(tour.id, tour.version)
      }

      let activationSkippedNotice: GuideTourSkippedStepNotice | null = null

      if (resolvedStepIndex > requestedStepIndex) {
        const entries = []

        for (
          let index = requestedStepIndex;
          index < resolvedStepIndex;
          index += 1
        ) {
          if (isGuideTourStepExpired(activeTour.steps[index])) {
            entries.push(
              createGuideTourSkippedStepEntry(activeTour, index, 'expired')
            )
          }
        }

        if (entries.length > 0) {
          activationSkippedNotice = {
            entries,
            allRemainingStepsUnavailable: false,
          }
        }
      }

      if (activationSkippedNotice) {
        setSkippedStepNotice(activationSkippedNotice)
        setIsSkippedStepNoticeVisible(true)
      } else if (!isResume && !tourUnavailableSummaryLatchedRef.current) {
        clearSkippedStepNotice()
      }

      beginRouteTourChainSession(currentPathnameRef.current, registry)

      tourSessionAutoStartRef.current = options?.autoStart === true

      dispatch({
        type: 'START',
        tour: activeTour,
        stepIndex: resolvedStepIndex,
        viewedStepCount: options?.viewedStepCount,
      })
    },
    [registry, clearSkippedStepNotice]
  )

  const enterGuideTourUnavailableSummary = useCallback(() => {
    if (tourUnavailableSummaryLatchedRef.current) {
      return
    }

    const tour = stateRef.current.activeTour

    if (!tour) {
      return
    }

    tourUnavailableSummaryLatchedRef.current = true
    notifyGuideTourUnavailableOnPage(tour, currentPathnameRef.current)
    setSkippedStepNotice({
      entries: [],
      allRemainingStepsUnavailable: false,
      tourUnavailableOnPage: true,
    })
    setIsSkippedStepsSummaryActive(true)
    setIsSkippedStepNoticeVisible(true)
  }, [])

  const startGuideTourWhenReady = useCallback(
    (
      tourId: string,
      options: {
        stepIndex: number
        viewedStepCount: number
        resume?: boolean
        autoStart?: boolean
      }
    ) => {
      const tour = registry.getGuideTour(tourId)

      if (!tour) {
        return
      }

      if (isGuideTourSuppressedOnMobile(tour)) {
        return
      }

      setIsGuideTourActivationPending(true)

      const clearActivationPending = () => {
        setIsGuideTourActivationPending(false)
      }

      const activeTour = resolveGuideTourForRuntime(tour)
      const targetId = activeTour.steps[options.stepIndex]?.targetId?.trim()
      const isFirstStepLaunch = options.viewedStepCount === 0
      const missingActivationTarget =
        isFirstStepLaunch &&
        Boolean(targetId) &&
        isGuideTourActivationStepTargetMissing(activeTour, options.stepIndex)

      const activationMaxWaitMs = missingActivationTarget
        ? GUIDE_TOUR_TARGET_MISSING_DELAY_MS
        : GUIDE_TOUR_STEP_ACTIVATION_MAX_WAIT_MS

      const finishActivation = (ready: boolean) => {
        try {
          if (ready) {
            start(tourId, options)
            return
          }

          const stepTargetId =
            activeTour.steps[options.stepIndex]?.targetId?.trim()

          if (
            isFirstStepLaunch &&
            stepTargetId &&
            isGuideTourActivationStepTargetMissing(
              activeTour,
              options.stepIndex
            )
          ) {
            if (!tourUnavailableSummaryLatchedRef.current) {
              tourUnavailableSummaryLatchedRef.current = true
              notifyGuideTourUnavailableOnPage(
                activeTour,
                currentPathnameRef.current
              )
              setSkippedStepNotice({
                entries: [],
                allRemainingStepsUnavailable: false,
                tourUnavailableOnPage: true,
              })
              setIsSkippedStepsSummaryActive(true)
              setIsSkippedStepNoticeVisible(true)
            }

            start(tourId, options)
            return
          }

          if (
            isFirstStepLaunch &&
            stepTargetId &&
            getGuideTourTargetAvailability(stepTargetId) === 'unmeasurable'
          ) {
            start(tourId, options)
            return
          }

          clearGuideTourProgress(tour.id, tour.version)
          refreshGuideTourNotifications()
        } finally {
          clearActivationPending()
        }
      }

      if (missingActivationTarget) {
        if (guideTourActivationTimeoutRef.current !== null) {
          window.clearTimeout(guideTourActivationTimeoutRef.current)
        }

        guideTourActivationTimeoutRef.current = window.setTimeout(() => {
          guideTourActivationTimeoutRef.current = null
          finishActivation(false)
        }, GUIDE_TOUR_TARGET_MISSING_DELAY_MS)
        return
      }

      waitForGuideTourStepActivatable(activeTour, options.stepIndex, {
        maxWaitMs: activationMaxWaitMs,
      })
        .then(finishActivation)
        .catch(() => {
          clearGuideTourProgress(tour.id, tour.version)
          refreshGuideTourNotifications()
          clearActivationPending()
        })
    },
    [registry, refreshGuideTourNotifications, start]
  )

  const launchGuideTour = useCallback(
    (tourId: string, mode: GuideTourLaunchMode) => {
      const tour = registry.getGuideTour(tourId)

      if (!tour) {
        return
      }

      if (isGuideTourSuppressedOnMobile(tour)) {
        return
      }

      const activeTour = resolveGuideTourForRuntime(tour)

      if (stateRef.current.status === 'active') {
        const currentlyActiveTour = stateRef.current.activeTour

        if (currentlyActiveTour) {
          closeGuideTourModalIfOpenedByForceClick(
            currentlyActiveTour,
            stateRef.current.currentStepIndex
          )
          finalizeGuideTourForcedScrollOnExit(
            currentlyActiveTour,
            stateRef.current.currentStepIndex,
            currentPathnameRef.current,
            registry,
            'dismissed'
          )
        }

        dispatch({ type: 'DISMISS' })
      }

      dismissedTourRef.current = clearGuideTourDismissalForTour(
        dismissedTourRef.current,
        tourId,
        tour.version
      )
      routeTourPausedPathnameRef.current = null
      clearRouteTourChainSession()

      tourUnavailableSummaryLatchedRef.current = false
      setIsSkippedStepsSummaryActive(false)

      let stepIndex: number
      let viewedStepCount: number

      if (mode === 'restart') {
        notifyStepInteraction('restart')
        clearGuideTourProgress(tour.id, tour.version)
        stepIndex = findNextAvailableGuideTourStepIndex(activeTour, 0) ?? 0
        viewedStepCount = 0

        if (!isGuideTourActivationStepTargetMissing(activeTour, stepIndex)) {
          notifyStepInteraction('restart')
        }
      } else {
        const progress = getGuideTourProgress(tour.id, tour.version)

        if (!progress) {
          return
        }

        stepIndex =
          resolveGuideTourActivationStepIndex(
            activeTour,
            progress.currentStepIndex
          ) ?? progress.currentStepIndex
        viewedStepCount = progress.viewedStepCount
      }

      const step = activeTour.steps[stepIndex]
      const targetRoute =
        (step && getGuideTourStepRoute(activeTour, step)) ||
        currentPathnameRef.current
      const pathname =
        currentPathnameRef.current ||
        (typeof window !== 'undefined' ? window.location.pathname : '/')
      const launchOptions = {
        stepIndex,
        viewedStepCount,
        resume: mode === 'continue',
      }

      if (matchGuideTourRoute(targetRoute, pathname)) {
        startGuideTourWhenReady(tourId, launchOptions)
        return
      }

      setPendingGuideTourLaunch({
        tourId,
        stepIndex,
        viewedStepCount,
        targetRoute,
        resume: mode === 'continue',
      })
    },
    [registry, startGuideTourWhenReady, notifyStepInteraction]
  )

  const next = useCallback(() => {
    clearSkippedStepNotice()

    const currentState = stateRef.current
    const tour = currentState.activeTour
    const stepIndex = currentState.currentStepIndex
    const step = getCurrentGuideTourStep(currentState)
    const willComplete =
      tour !== null && stepIndex === tour.steps.length - 1 && !step?.forceClick

    if (tour && willComplete) {
      closeGuideTourModalIfOpenedByForceClick(tour, stepIndex)
      finalizeGuideTourForcedScrollOnExit(
        tour,
        stepIndex,
        currentPathnameRef.current,
        registry,
        'completed'
      )

      if (currentState.viewedStepCount < countAvailableGuideTourSteps(tour)) {
        dispatch({ type: 'MARK_STEP_VIEWED' })
      }

      finishGuideTour(tour.id, tour.version)
      refreshGuideTourNotifications()
      dispatch({ type: 'COMPLETE' })
      return
    }

    const shouldDeferAdvance = Boolean(step?.forceClick)

    if (step?.forceClick) {
      forceClickGuideTourTarget(step.forceClick)
    }

    const advance = () => {
      dispatch({ type: 'NEXT' })
    }

    if (shouldDeferAdvance) {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(advance)
      })
      return
    }

    advance()
  }, [registry, clearSkippedStepNotice, refreshGuideTourNotifications])

  const skipUnavailableGuideTourStep = useCallback(
    (skipKind: GuideTourUnavailableStepSkipKind = 'missing') => {
      if (tourUnavailableSummaryLatchedRef.current) {
        return
      }

      return createGuideTourUnavailableStepSkipper({
        getState: () => stateRef.current,
        pathname: currentPathnameRef.current,
        advance: () => {
          stateRef.current = guideTourReducer(stateRef.current, {
            type: 'NEXT',
          })
          dispatch({ type: 'NEXT' })
        },
        completeLastStep: (tour, stepIndex) => {
          closeGuideTourModalIfOpenedByForceClick(tour, stepIndex)
          finalizeGuideTourForcedScrollOnExit(
            tour,
            stepIndex,
            currentPathnameRef.current,
            registry,
            'completed'
          )
        },
        onRecordSkippedStep: (stepIndex, recordedSkipKind) => {
          recordAutoSkippedStep(stepIndex, recordedSkipKind)
        },
        onStepAutoSkipped: () => {
          notifyStepInteraction('skipped')
        },
        onSkippedStepsWillComplete: ({ skipKind }) => {
          const currentState = stateRef.current

          if (currentState.viewedStepCount === 0 && skipKind === 'missing') {
            enterGuideTourUnavailableSummary()
            return true
          }

          setSkippedStepNotice(current => ({
            entries: current?.entries ?? [],
            allRemainingStepsUnavailable: true,
          }))
          setIsSkippedStepsSummaryActive(true)
          setIsSkippedStepNoticeVisible(true)
          return true
        },
      })(skipKind)
    },
    [
      enterGuideTourUnavailableSummary,
      notifyStepInteraction,
      recordAutoSkippedStep,
      registry,
    ]
  )

  const completeSkippedStepsSummary = useCallback(
    (display?: GuideTourStepInteractionDisplay) => {
      notifyStepInteraction('complete', display)
      const currentState = stateRef.current
      const tour = currentState.activeTour
      const stepIndex = currentState.currentStepIndex

      if (tour) {
        closeGuideTourModalIfOpenedByForceClick(tour, stepIndex)
        finalizeGuideTourForcedScrollOnExit(
          tour,
          stepIndex,
          currentPathnameRef.current,
          registry,
          'completed'
        )
        finishGuideTour(tour.id, tour.version)
        refreshGuideTourNotifications()
      }

      clearSkippedStepNotice()
      dispatch({ type: 'COMPLETE' })
    },
    [
      clearSkippedStepNotice,
      notifyStepInteraction,
      registry,
      refreshGuideTourNotifications,
    ]
  )

  const prev = useCallback(() => {
    const currentState = stateRef.current
    const tour = currentState.activeTour

    if (!tour || currentState.status !== 'active') {
      return
    }

    const pathname = currentPathnameRef.current
    const targetIndex = findPreviousReachableGuideTourStepIndex(
      tour,
      currentState.currentStepIndex - 1,
      pathname
    )

    if (targetIndex === null) {
      setSkippedStepNotice({
        entries: [],
        allRemainingStepsUnavailable: false,
        noPreviousStepsAvailable: true,
      })
      setIsSkippedStepNoticeVisible(true)
      return
    }

    clearSkippedStepNotice()

    let skippedNotice: GuideTourSkippedStepNotice | null = null

    for (
      let index = currentState.currentStepIndex - 1;
      index > targetIndex;
      index -= 1
    ) {
      const reason = isGuideTourStepExpired(tour.steps[index])
        ? 'expired'
        : 'unavailable-target'

      skippedNotice = appendGuideTourSkippedStepEntry(
        skippedNotice,
        createGuideTourSkippedStepEntry(tour, index, reason)
      )
    }

    if (skippedNotice) {
      setSkippedStepNotice(skippedNotice)
      setIsSkippedStepNoticeVisible(true)
    }

    dispatch({ type: 'GO_TO_STEP', stepIndex: targetIndex })
  }, [clearSkippedStepNotice])

  const skip = useCallback(() => {
    clearSkippedStepNotice()

    const currentState = stateRef.current
    const tour = currentState.activeTour

    if (tour) {
      closeGuideTourModalIfOpenedByForceClick(
        tour,
        currentState.currentStepIndex
      )
      finalizeGuideTourForcedScrollOnExit(
        tour,
        currentState.currentStepIndex,
        currentPathnameRef.current,
        registry,
        'skipped'
      )
    }

    dispatch({ type: 'SKIP' })
  }, [clearSkippedStepNotice, registry])

  const dismiss = useCallback(() => {
    setIsBlockedTargetInteractionWarningVisible(false)

    const currentState = stateRef.current
    const tour = currentState.activeTour
    const summaryActive = isSkippedStepsSummaryActive

    clearSkippedStepNotice()

    if (!tour) {
      routeTourPausedPathnameRef.current = currentPathnameRef.current
      dispatch({ type: 'DISMISS' })
      return
    }

    const progress = {
      currentStepIndex: currentState.currentStepIndex,
      viewedStepCount: currentState.viewedStepCount,
    }
    const shouldCompleteInstead =
      currentState.status === 'active' &&
      shouldTreatGuideTourAsCompleted(tour, progress, { summaryActive })

    closeGuideTourModalIfOpenedByForceClick(tour, currentState.currentStepIndex)
    finalizeGuideTourForcedScrollOnExit(
      tour,
      currentState.currentStepIndex,
      currentPathnameRef.current,
      registry,
      shouldCompleteInstead ? 'completed' : 'dismissed'
    )

    if (shouldCompleteInstead) {
      if (currentState.viewedStepCount < countAvailableGuideTourSteps(tour)) {
        dispatch({ type: 'MARK_STEP_VIEWED' })
      }

      finishGuideTour(tour.id, tour.version)
      refreshGuideTourNotifications()
      dispatch({ type: 'COMPLETE' })
      return
    }

    dismissedTourRef.current = {
      tourId: tour.id,
      version: tour.version,
      pathname: currentPathnameRef.current,
      gatesReady: isCurrentStepReady && (!hasTarget || targetBounds !== null),
    }

    if (currentState.status === 'active') {
      setGuideTourProgress(tour.id, tour.version, progress)
      refreshGuideTourNotifications()
    }

    routeTourPausedPathnameRef.current = currentPathnameRef.current
    dispatch({ type: 'DISMISS' })
  }, [
    clearSkippedStepNotice,
    isCurrentStepReady,
    hasTarget,
    targetBounds,
    isSkippedStepsSummaryActive,
    registry,
    refreshGuideTourNotifications,
  ])

  const interrupt = useCallback(() => {
    if (tourUnavailableSummaryLatchedRef.current) {
      dismissTourUnavailableOnPage()
      return
    }

    notifyStepInteraction('interrupt')
    dismiss()
  }, [dismiss, dismissTourUnavailableOnPage, notifyStepInteraction])

  const setCurrentPathnameWithRouteEffects = useCallback((pathname: string) => {
    if (currentPathnameRef.current !== pathname) {
      clearGuideTourRouteScrollState(currentPathnameRef.current)
      clearRouteTourChainSession()
      routeTourPausedPathnameRef.current = null
    }

    currentPathnameRef.current = pathname
    setCurrentPathname(pathname)
    dismissedTourRef.current = clearGuideTourDismissalOutsidePath(
      dismissedTourRef.current,
      pathname
    )
  }, [])

  const isRouteTourPaused = useCallback(
    (pathname: string) => routeTourPausedPathnameRef.current === pathname,
    []
  )

  const isRouteTourChainCooldownActive = useCallback(
    () => isGuideTourRouteChainCooldownActive(routeTourChainCooldownUntil),
    [routeTourChainCooldownUntil]
  )

  const getRouteTourQueue = useCallback(
    (pathname: string, activeTourId: string) =>
      getRouteTourChainSessionInfo(pathname, activeTourId) ??
      getRouteTourQueueInfo(pathname, registry, activeTourId),
    [registry]
  )

  const isTourDismissedOnPath = useCallback(
    (tourId: string, version: number, pathname: string) =>
      isGuideTourDismissedOnPath(
        dismissedTourRef.current,
        tourId,
        version,
        pathname
      ),
    []
  )

  const complete = useCallback(() => {
    const currentState = stateRef.current
    const tour = currentState.activeTour

    if (tour) {
      finishGuideTour(tour.id, tour.version)
      refreshGuideTourNotifications()
    }

    dispatch({ type: 'COMPLETE' })
  }, [refreshGuideTourNotifications])

  const markStepViewed = useCallback(() => {
    dispatch({ type: 'MARK_STEP_VIEWED' })
  }, [])

  const reset = useCallback(() => {
    dispatch({ type: 'RESET' })
  }, [])

  const notifyBlockedTargetInteraction = useCallback(() => {
    setIsBlockedTargetInteractionWarningVisible(true)
    setBlockedTargetInteractionFeedbackKey(key => key + 1)
  }, [])

  const clearBlockedTargetInteractionWarning = useCallback(() => {
    setIsBlockedTargetInteractionWarningVisible(false)
  }, [])

  useEffect(() => {
    setBlockedTargetInteractionFeedbackKey(0)
    setIsBlockedTargetInteractionWarningVisible(false)
  }, [state.activeTourId, state.currentStepIndex])

  useEffect(() => {
    if (!isSkippedStepNoticeVisible) {
      return undefined
    }

    if (
      isSkippedStepsSummaryActive &&
      skippedStepNotice?.tourUnavailableOnPage
    ) {
      return undefined
    }

    const timeoutId = window.setTimeout(() => {
      setIsSkippedStepNoticeVisible(false)
    }, getGuideTourSkippedStepNoticeMs())

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [
    isSkippedStepNoticeVisible,
    isSkippedStepsSummaryActive,
    skippedStepNotice?.tourUnavailableOnPage,
  ])

  useEffect(() => {
    if (!isBlockedTargetInteractionWarningVisible) {
      return undefined
    }

    const timeoutId = window.setTimeout(() => {
      setIsBlockedTargetInteractionWarningVisible(false)
    }, GUIDE_TOUR_BLOCKED_TARGET_INTERACTION_WARNING_MS)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [isBlockedTargetInteractionWarningVisible])

  const stepGateOptions =
    state.activeTour !== null
      ? getGuideTourStepGateOptions(state.activeTour, state.currentStepIndex)
      : null

  useGuideTourMissingTargetSkip({
    enabled:
      state.status === 'active' &&
      Boolean(targetId) &&
      !isSkippedStepsSummaryActive,
    targetId,
    targetBounds,
    stepIndex: state.currentStepIndex,
    isStepGateReady: isCurrentStepReady,
    modalVisibleGate: stepGateOptions?.modalVisibleGate,
    allowUnreachableAutoSkip: true,
    onSkip: skipUnavailableGuideTourStep,
  })

  useEffect(() => {
    if (state.status === 'active' && state.activeTour) {
      if (
        isGuideTourStepInForceClickModalFlow(
          state.activeTour,
          state.currentStepIndex
        )
      ) {
        activeModalFlowRef.current = {
          tour: state.activeTour,
          stepIndex: state.currentStepIndex,
        }
      } else {
        activeModalFlowRef.current = null
      }

      return
    }

    const snapshot = activeModalFlowRef.current

    if (!snapshot) {
      return
    }

    closeGuideTourModalIfOpenedByForceClick(snapshot.tour, snapshot.stepIndex)
    activeModalFlowRef.current = null
  }, [state.status, state.activeTour, state.currentStepIndex])

  useEffect(() => {
    releaseOrphanedGuideTourPageScrollLock()
  }, [])

  useEffect(() => {
    const shouldLockScroll =
      state.status === 'active' &&
      !isSkippedStepsSummaryActive &&
      isCurrentStepReady &&
      (!hasTarget ||
        targetBounds !== null ||
        (forceScrollToTarget && isGuideTourPageScrollLocked()))

    if (!shouldLockScroll) {
      return undefined
    }

    const unlockScroll = lockGuideTourPageScroll()

    return () => {
      unlockScroll()
    }
  }, [
    state.status,
    isCurrentStepReady,
    hasTarget,
    targetBounds,
    forceScrollToTarget,
    isSkippedStepsSummaryActive,
  ])

  useEffect(() => {
    const currentState = stateRef.current

    if (currentState.status !== 'active' || !currentState.activeTour) {
      return
    }

    const step = getCurrentGuideTourStep(currentState)

    if (!step || !isGuideTourStepExpired(step)) {
      return
    }

    const resolvedStepIndex = findNextAvailableGuideTourStepIndex(
      currentState.activeTour,
      currentState.currentStepIndex
    )

    if (resolvedStepIndex === null) {
      dispatch({ type: 'COMPLETE' })
      return
    }

    if (resolvedStepIndex !== currentState.currentStepIndex) {
      for (
        let index = currentState.currentStepIndex;
        index < resolvedStepIndex;
        index += 1
      ) {
        if (isGuideTourStepExpired(currentState.activeTour.steps[index])) {
          recordAutoSkippedStep(index, 'expired')
        }
      }

      dispatch({ type: 'GO_TO_STEP', stepIndex: resolvedStepIndex })
    }
  }, [
    state.activeTour,
    state.currentStepIndex,
    state.status,
    recordAutoSkippedStep,
  ])

  useEffect(() => {
    const tour = state.activeTour

    if (!tour) {
      return
    }

    if (state.status !== 'completed' && state.status !== 'skipped') {
      if (state.status === 'active' && !isSkippedStepsSummaryActive) {
        setGuideTourProgress(tour.id, tour.version, {
          currentStepIndex: state.currentStepIndex,
          viewedStepCount: state.viewedStepCount,
        })
        refreshGuideTourNotifications()
      }

      return
    }

    finishGuideTour(tour.id, tour.version)
    refreshGuideTourNotifications()
    closeGuideTourModalIfOpenedByForceClick(tour, state.currentStepIndex)

    const exitKind = state.status === 'skipped' ? 'skipped' : 'completed'
    const endingPathname = currentPathnameRef.current

    if (
      shouldRestoreGuideTourOriginRouteOnExit(tour, endingPathname, exitKind)
    ) {
      const originRoute = getGuideTourOriginRoute(tour)

      if (originRoute) {
        setOriginRouteRestore(originRoute)
        routeTourPausedPathnameRef.current = endingPathname
      }
    }

    finalizeGuideTourForcedScrollOnExit(
      tour,
      state.currentStepIndex,
      endingPathname,
      registry,
      exitKind
    )

    if (
      !hasMoreUnseenRouteToursAfter(currentPathnameRef.current, registry, tour)
    ) {
      clearRouteTourChainSession()
    }

    const cooldownUntil = Date.now() + GUIDE_TOUR_ROUTE_CHAIN_COOLDOWN_MS
    routeTourChainCooldownUntilRef.current = cooldownUntil
    setRouteTourChainCooldownUntil(cooldownUntil)

    window.setTimeout(() => {
      setRouteTourChainCooldownUntil(0)
      routeTourChainCooldownUntilRef.current = 0
    }, GUIDE_TOUR_ROUTE_CHAIN_COOLDOWN_MS)

    window.requestAnimationFrame(() => {
      dispatch({ type: 'RESET' })
    })
  }, [
    state.activeTour,
    state.status,
    state.currentStepIndex,
    state.viewedStepCount,
    isSkippedStepsSummaryActive,
    registry,
    refreshGuideTourNotifications,
  ])

  const value = useMemo<GuideTourContextValue>(
    () => ({
      registry,
      targetBounds,
      forceClickTargetBounds,
      isTourDismissedOnPath,
      isRouteTourPaused,
      isRouteTourChainCooldownActive,
      getRouteTourQueue,
      currentPathname,
      isCurrentStepReady,
      isInModalLayer,
      state,
      currentStep: getCurrentGuideTourStep(state),
      currentStepNumber: state.activeTour
        ? getAvailableGuideTourStepNumber(
            state.activeTour,
            state.currentStepIndex
          )
        : 0,
      stepCount: state.activeTour
        ? countAvailableGuideTourSteps(state.activeTour)
        : 0,
      isFirstStep: state.activeTour
        ? !hasPreviousReachableGuideTourStep(
            state.activeTour,
            state.currentStepIndex,
            currentPathname
          )
        : true,
      isLastStep: isGuideTourLastStep(state),
      shouldCompleteOnContinue: shouldGuideTourCompleteOnNext(state),
      canGoNext: canGoToNextGuideTourStep(state),
      canGoPrev: state.activeTour
        ? hasPreviousReachableGuideTourStep(
            state.activeTour,
            state.currentStepIndex,
            currentPathname
          )
        : false,
      getGuideTour,
      start,
      startWhenReady: startGuideTourWhenReady,
      next,
      prev,
      skip,
      dismiss,
      interrupt,
      notifyStepInteraction,
      setCurrentPathname: setCurrentPathnameWithRouteEffects,
      complete,
      markStepViewed,
      reset,
      blockedTargetInteractionFeedbackKey,
      isBlockedTargetInteractionWarningVisible,
      notifyBlockedTargetInteraction,
      clearBlockedTargetInteractionWarning,
      skippedStepNotice,
      isSkippedStepNoticeVisible,
      isSkippedStepsSummaryActive,
      clearSkippedStepNotice,
      dismissTourUnavailableOnPage,
      completeSkippedStepsSummary,
      originRouteRestore,
      clearOriginRouteRestore,
      pendingGuideTourLaunch,
      clearPendingGuideTourLaunch,
      guideTourNotifications,
      guideTourNotificationsRevision: notificationsRevision,
      refreshGuideTourNotifications,
      getGuideTourNotifications,
      getInProgressGuideTours,
      launchGuideTour,
      isGuideTourActivationPending,
    }),
    [
      registry,
      targetBounds,
      forceClickTargetBounds,
      isTourDismissedOnPath,
      isRouteTourPaused,
      isRouteTourChainCooldownActive,
      getRouteTourQueue,
      currentPathname,
      isCurrentStepReady,
      isInModalLayer,
      state,
      getGuideTour,
      start,
      startGuideTourWhenReady,
      next,
      prev,
      skip,
      dismiss,
      interrupt,
      notifyStepInteraction,
      setCurrentPathnameWithRouteEffects,
      complete,
      markStepViewed,
      reset,
      blockedTargetInteractionFeedbackKey,
      isBlockedTargetInteractionWarningVisible,
      notifyBlockedTargetInteraction,
      clearBlockedTargetInteractionWarning,
      skippedStepNotice,
      isSkippedStepNoticeVisible,
      isSkippedStepsSummaryActive,
      clearSkippedStepNotice,
      dismissTourUnavailableOnPage,
      completeSkippedStepsSummary,
      originRouteRestore,
      clearOriginRouteRestore,
      pendingGuideTourLaunch,
      clearPendingGuideTourLaunch,
      guideTourNotifications,
      notificationsRevision,
      refreshGuideTourNotifications,
      getGuideTourNotifications,
      getInProgressGuideTours,
      launchGuideTour,
      isGuideTourActivationPending,
    ]
  )

  return (
    <GuideTourContext.Provider value={value}>
      {children}
      <GuideTourOverlay />
      <GuideTourForceClickAffordance />
      <GuideTourSpotlightShield />
      <GuideTourActivationLoadingCard />
      <GuideTourStepCard />
    </GuideTourContext.Provider>
  )
}
